import type { BackendRegistration, EmulatorBackend, SystemId } from './types';
import { FrameCoreBackend } from './backends/FrameCoreBackend';
import { WorkerBackend } from './backends/WorkerBackend';
import { TestPatternCore } from './cores/TestPatternCore';
import { LibretroWasmCore, type LibretroCoreConfig } from './cores/LibretroWasmCore';

/**
 * 機種ごとの推奨 libretro コア。
 * /public/cores に <coreName>_libretro.js が置かれていれば自動的に採用される。
 */
const LIBRETRO_CORES: Array<LibretroCoreConfig & { displayName: string; priority: number }> = [
  {
    coreName: 'gambatte',
    displayName: 'Gambatte (GB/GBC)',
    systems: ['gb'],
    priority: 100,
    variables: { gambatte_gb_colorization: 'auto' },
  },
  {
    coreName: 'fceumm',
    displayName: 'FCEUmm (NES)',
    systems: ['nes'],
    priority: 100,
  },
  {
    coreName: 'snes9x',
    displayName: 'Snes9x (SNES)',
    systems: ['snes'],
    priority: 100,
  },
  {
    // mGBA は GBA が本業。GB/GBC も動くが、そちらは Gambatte を優先する。
    coreName: 'mgba',
    displayName: 'mGBA (GBA/GB/GBC)',
    systems: ['gba', 'gb'],
    priority: 90,
    biosFilename: 'gba_bios.bin',
  },
  {
    coreName: 'vbam',
    displayName: 'VBA-M (GBA)',
    systems: ['gba'],
    priority: 80,
  },
  {
    coreName: 'nestopia',
    displayName: 'Nestopia (NES)',
    systems: ['nes'],
    priority: 90,
  },
  {
    coreName: 'pcsx_rearmed',
    displayName: 'PCSX-ReARMed (PS1)',
    systems: ['psx'],
    priority: 100,
    needsFilesystem: true,
    biosFilename: 'scph5501.bin',
    variables: {
      pcsx_rearmed_frameskip: '0',
      pcsx_rearmed_neon_interlace_enable: 'disabled',
      // RGB565 固定。フロントの変換と揃える（reload 後に効く）。
      pcsx_rearmed_rgb32_output: 'disabled',
      // auto: 実 BIOS を優先。HLE 強制にしない（カード容量判定が壊れる）。
      pcsx_rearmed_bios: 'auto',
      // shared: load 前に /saves/pcsx-card1.mcd を書き、SysReset 時点からカードが見える。
      pcsx_rearmed_memcard1: 'shared',
      pcsx_rearmed_memcard2: 'none',
    },
  },
  {
    coreName: 'mednafen_wswan',
    displayName: 'Beetle WonderSwan',
    systems: ['ws'],
    priority: 100,
  },
  {
    coreName: 'ppsspp',
    displayName: 'PPSSPP (PSP)',
    systems: ['psp'],
    priority: 100,
    needsFilesystem: true,
    variables: {
      // bare libretro は HW_RENDER 無し → SoftGPU + Interpreter
      ppsspp_cpu_core: 'Interpreter',
      ppsspp_software_rendering: 'enabled',
    },
  },
];

const registrations: BackendRegistration[] = [
  ...LIBRETRO_CORES.map<BackendRegistration>((cfg) => ({
    id: `libretro:${cfg.coreName}`,
    displayName: cfg.displayName,
    systems: cfg.systems,
    priority: cfg.priority,
    isAvailable: () => LibretroWasmCore.probe(cfg.coreName),
    create: () => new FrameCoreBackend(new LibretroWasmCore(cfg), cfg.displayName),
    coreSpec: { kind: 'libretro', config: stripDisplayFields(cfg) },
  })),
  {
    // 常に利用可能なフォールバック。ROM 無しでも全経路を検証できる。
    id: 'test-pattern',
    displayName: 'Built-in Test Core (診断用)',
    systems: ['gb', 'gba', 'nes', 'snes', 'psx', 'ws', 'psp'],
    priority: -1,
    isAvailable: async () => true,
    create: () => new FrameCoreBackend(new TestPatternCore(), 'Built-in Test Core'),
    coreSpec: { kind: 'test' },
  },
];

/** レジストリ用の表示情報を落として、Worker へ渡せる設定だけにする。 */
function stripDisplayFields(
  cfg: LibretroCoreConfig & { displayName: string; priority: number },
): Omit<LibretroCoreConfig, 'loadModule'> {
  const { displayName: _d, priority: _p, loadModule: _l, ...rest } = cfg;
  return rest;
}

const availabilityCache = new Map<string, Promise<boolean>>();

function availability(reg: BackendRegistration): Promise<boolean> {
  let p = availabilityCache.get(reg.id);
  if (!p) {
    p = reg.isAvailable().catch(() => false);
    availabilityCache.set(reg.id, p);
  }
  return p;
}

/** ある機種に対して実際に使えるバックエンドを優先度順で返す。 */
export async function listBackends(system: SystemId): Promise<BackendRegistration[]> {
  const candidates = registrations.filter((r) => r.systems.includes(system));
  const flags = await Promise.all(candidates.map(availability));
  return candidates
    .filter((_, i) => flags[i])
    .sort((a, b) => b.priority - a.priority);
}

/**
 * コアの実ファイル有無に依存しない「推奨バックエンド ID」。
 * セーブ取り込み時に test-pattern へ落ちると、あとで本物のコアで読めなくなる。
 */
export function preferredBackendIdFor(system: SystemId): string {
  const candidates = registrations
    .filter((r) => r.systems.includes(system) && r.id !== 'test-pattern')
    .sort((a, b) => b.priority - a.priority);
  return candidates[0]?.id ?? `libretro:${system}`;
}

/**
 * 機種に対する最良のバックエンドを生成する。
 * @param preferredId 設定で明示指定されたバックエンド ID
 */
export interface CreateBackendOptions {
  preferredId?: string | undefined;
  /**
   * エミュレーションを Worker で走らせる。
   * OffscreenCanvas と module worker に対応していない環境では
   * 黙ってメインスレッド版へ落とす。
   */
  useWorker?: boolean;
}

export function workerSupported(): boolean {
  return (
    typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function'
  );
}

export async function createBackend(
  system: SystemId,
  options: CreateBackendOptions = {},
): Promise<{ backend: EmulatorBackend; registration: BackendRegistration }> {
  const list = await listBackends(system);
  if (list.length === 0) {
    throw new Error(`${system} に使えるエミュレータコアがありません`);
  }
  const chosen = (options.preferredId && list.find((r) => r.id === options.preferredId)) || list[0]!;

  // ディスクイメージを扱うコア (PS1/PSP) は Worker を既定にする。
  // 遅延読み込みに使う FileReaderSync が Worker にしか無く、
  // メインスレッドだと 600MB の BIN を丸ごとメモリへ載せることになって
  // iOS Safari が落ちるため。設定で明示的に off にしていても、
  // ここは「落ちるより 1 フレーム遅い方がまし」と判断して優先する。
  const wantsWorker = options.useWorker === true || needsLazyDisc(chosen);

  if (wantsWorker && chosen.coreSpec && workerSupported()) {
    return {
      backend: new WorkerBackend(chosen.coreSpec, chosen.id, chosen.systems, chosen.displayName),
      registration: chosen,
    };
  }

  return { backend: chosen.create(), registration: chosen };
}

/**
 * このコアがディスクイメージを仮想 FS 経由で読むか。
 * 該当するコアは Worker で走らせないと巨大イメージを捌けない。
 */
function needsLazyDisc(reg: BackendRegistration): boolean {
  const spec = reg.coreSpec;
  return spec?.kind === 'libretro' && spec.config.needsFilesystem === true;
}

/** 設定画面用: 全機種のコア導入状況 */
export async function coreStatus(): Promise<
  Array<{ system: SystemId; backends: Array<{ id: string; displayName: string; available: boolean }> }>
> {
  const systems: SystemId[] = ['gb', 'gba', 'nes', 'snes', 'psx', 'ws', 'psp'];
  return Promise.all(
    systems.map(async (system) => {
      const candidates = registrations.filter((r) => r.systems.includes(system));
      const flags = await Promise.all(candidates.map(availability));
      return {
        system,
        backends: candidates.map((r, i) => ({
          id: r.id,
          displayName: r.displayName,
          available: flags[i] ?? false,
        })),
      };
    }),
  );
}

/** 開発時にコアを追加配置したあと、可用性判定をやり直す。 */
export function invalidateAvailability(): void {
  availabilityCache.clear();
}
